import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Building2, Users, Plus, Trash2, PhoneCall, UserPlus, Pencil, KeyRound, Eye } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/lib/auth";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export function AdminOverview() {
  const [ws, setWs] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([api.get("/admin/workspaces"), api.get("/admin/users")])
      .then(([w, u]) => { setWs(w.data); setUsers(u.data); })
      .finally(() => setLoading(false));
  }, []);

  const totalCalls = ws.reduce((a, w) => a + (w.call_count || 0), 0);
  const totalLeads = ws.reduce((a, w) => a + (w.lead_count || 0), 0);

  return (
    <div className="space-y-6" data-testid="admin-overview-page">
      <div>
        <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Admin</p>
        <h1 className="text-3xl font-bold tracking-tight mt-1">Overview</h1>
        <p className="text-sm text-muted-foreground mt-1">Manage all client workspaces, users and Call Swiper agent mappings.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard icon={Building2} label="Workspaces" value={ws.length} />
        <StatCard icon={Users} label="Users" value={users.length} />
        <StatCard icon={PhoneCall} label="Total calls" value={totalCalls} />
        <StatCard icon={UserPlus} label="Total leads" value={totalLeads} />
      </div>

      <Card className="p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold">Recent workspaces</h3>
        </div>
        <Table>
          <TableHeader>
            <TableRow className="border-border hover:bg-transparent">
              <TableHead className="text-[10px] uppercase tracking-wider">Name</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider">Agents</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider">Users</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider">Calls</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider">Leads</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {ws.map((w) => (
              <TableRow key={w.id} className="border-border">
                <TableCell className="font-semibold text-sm">{w.name}</TableCell>
                <TableCell className="text-xs font-mono text-muted-foreground">{(w.agent_ids || []).length}</TableCell>
                <TableCell className="text-sm">{w.user_count}</TableCell>
                <TableCell className="text-sm">{w.call_count}</TableCell>
                <TableCell className="text-sm">{w.lead_count}</TableCell>
              </TableRow>
            ))}
            {!loading && ws.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-6 text-sm text-muted-foreground">No workspaces yet.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

function StatCard({ icon: Icon, label, value }) {
  return (
    <Card className="p-4 flex items-start justify-between">
      <div>
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
        <div className="text-2xl font-bold tracking-tight mt-1">{value}</div>
      </div>
      <div className="w-8 h-8 flex items-center justify-center rounded-md bg-primary/10 border border-primary/25 text-primary"><Icon className="w-4 h-4" /></div>
    </Card>
  );
}

// ---------- Workspaces Page ----------
export function AdminWorkspaces() {
  const navigate = useNavigate();
  const { impersonateWorkspace } = useAuth();
  const [ws, setWs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", agent_ids: "" });
  const [impersonateTarget, setImpersonateTarget] = useState(null);
  const [impersonating, setImpersonating] = useState(false);

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/admin/workspaces");
    setWs(data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const doImpersonate = async () => {
    if (!impersonateTarget) return;
    setImpersonating(true);
    try {
      await impersonateWorkspace(impersonateTarget.id);
      toast.success(`Now viewing as ${impersonateTarget.name}`);
      setImpersonateTarget(null);
      navigate("/app/calls");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to start impersonation");
    } finally {
      setImpersonating(false);
    }
  };

  const create = async (e) => {
    e.preventDefault();
    try {
      const agentIds = form.agent_ids.split(",").map((s) => s.trim()).filter(Boolean);
      await api.post("/admin/workspaces", { name: form.name, agent_ids: agentIds });
      toast.success("Workspace created");
      setCreateOpen(false);
      setForm({ name: "", agent_ids: "" });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    try {
      const agentIds = form.agent_ids.split(",").map((s) => s.trim()).filter(Boolean);
      await api.patch(`/admin/workspaces/${editing.id}`, { name: form.name, agent_ids: agentIds });
      toast.success("Workspace updated");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete workspace? Users will be unassigned.")) return;
    try {
      await api.delete(`/admin/workspaces/${id}`);
      toast.success("Deleted");
      load();
    } catch { toast.error("Failed"); }
  };

  const startEdit = (w) => {
    setEditing(w);
    setForm({ name: w.name, agent_ids: (w.agent_ids || []).join(", ") });
  };

  return (
    <div className="space-y-6" data-testid="admin-workspaces-page">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Admin</p>
          <h1 className="text-3xl font-bold tracking-tight mt-1">Workspaces</h1>
          <p className="text-sm text-muted-foreground mt-1">Create client workspaces and map Call Swiper assistant IDs.</p>
        </div>
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button data-testid="create-workspace-btn"><Plus className="w-4 h-4 mr-1.5" /> New workspace</Button>
          </DialogTrigger>
          <DialogContent data-testid="create-workspace-modal">
            <DialogHeader>
              <DialogTitle>Create workspace</DialogTitle>
              <DialogDescription>Give the workspace a name and (optionally) map one or more Call Swiper assistant IDs.</DialogDescription>
            </DialogHeader>
            <form onSubmit={create} className="space-y-4">
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Name</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="workspace-name-input" placeholder="Acme Corp" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Call Swiper Agent IDs</Label>
                <Input value={form.agent_ids} onChange={(e) => setForm({ ...form, agent_ids: e.target.value })} data-testid="workspace-agents-input" placeholder="asst_123, asst_456" className="font-mono text-sm" />
                <p className="text-xs text-muted-foreground mt-1">Comma-separated Call Swiper assistant IDs</p>
              </div>
              <DialogFooter>
                <Button type="submit" data-testid="workspace-save-btn">Create</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="p-0 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-border hover:bg-transparent">
              <TableHead className="text-[10px] uppercase tracking-wider">Workspace</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider">Call Swiper Agent IDs</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider">Users</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider">Calls</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider">Leads</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {ws.map((w) => (
              <TableRow key={w.id} className="border-border hover:bg-muted/40" data-testid={`workspace-row-${w.id}`}>
                <TableCell>
                  <div className="font-semibold text-sm">{w.name}</div>
                  <div className="text-[10px] font-mono text-muted-foreground">{w.id.slice(0, 8)}</div>
                </TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1 max-w-[280px]">
                    {(w.agent_ids || []).length === 0 && <span className="text-xs text-muted-foreground italic">None mapped</span>}
                    {(w.agent_ids || []).map((a) => (
                      <Badge key={a} variant="outline" className="font-mono text-[10px]">{a.slice(0, 14)}…</Badge>
                    ))}
                  </div>
                </TableCell>
                <TableCell className="text-sm">{w.user_count}</TableCell>
                <TableCell className="text-sm">{w.call_count}</TableCell>
                <TableCell className="text-sm">{w.lead_count}</TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    <Button size="sm" variant="outline" onClick={() => setImpersonateTarget(w)} data-testid={`workspace-impersonate-btn-${w.id}`} title="Login as this workspace">
                      <Eye className="w-3.5 h-3.5 mr-1.5" /> Login as
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => startEdit(w)} data-testid={`workspace-edit-btn-${w.id}`}>Edit</Button>
                    <Button size="sm" variant="outline" onClick={() => del(w.id)} data-testid={`workspace-delete-btn-${w.id}`} className="text-destructive"><Trash2 className="w-3.5 h-3.5" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {!loading && ws.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-8 text-sm text-muted-foreground">No workspaces yet.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent data-testid="edit-workspace-modal">
          <DialogHeader>
            <DialogTitle>Edit workspace</DialogTitle>
          </DialogHeader>
          {editing && (
            <form onSubmit={saveEdit} className="space-y-4">
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Name</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Call Swiper Agent IDs</Label>
                <Input value={form.agent_ids} onChange={(e) => setForm({ ...form, agent_ids: e.target.value })} className="font-mono text-sm" />
              </div>
              <DialogFooter>
                <Button type="submit">Save</Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Impersonation confirmation */}
      <AlertDialog open={!!impersonateTarget} onOpenChange={(o) => !o && setImpersonateTarget(null)}>
        <AlertDialogContent data-testid="confirm-impersonate-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Login as this workspace?</AlertDialogTitle>
            <AlertDialogDescription>
              {impersonateTarget && (
                <>
                  You are about to enter <span className="font-semibold">{impersonateTarget.name}</span> in <span className="font-semibold">impersonation mode</span>.
                  You will see the client dashboard exactly as this workspace's users see it. All access is audit-logged.
                  You can return to the admin console anytime from the banner at the top.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-impersonate-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doImpersonate} disabled={impersonating} data-testid="confirm-impersonate-confirm">
              <Eye className="w-3.5 h-3.5 mr-1.5" /> Continue
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ---------- Users Page ----------
export function AdminUsers() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [ws, setWs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", name: "", role: "client", workspace_id: "" });

  // Edit state
  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", email: "", role: "client", workspace_id: "", status: "active" });
  const [editErrors, setEditErrors] = useState({});
  const [editBusy, setEditBusy] = useState(false);
  const [confirmSaveOpen, setConfirmSaveOpen] = useState(false);

  // Password reset state
  const [resetOpen, setResetOpen] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);
  const [resetForm, setResetForm] = useState({ pw: "", pw2: "" });
  const [resetBusy, setResetBusy] = useState(false);

  // Delete confirm
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = async () => {
    setLoading(true);
    const [u, w] = await Promise.all([api.get("/admin/users"), api.get("/admin/workspaces")]);
    setUsers(u.data); setWs(w.data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/users", { ...form, workspace_id: form.workspace_id || null });
      toast.success("User created successfully.");
      setCreateOpen(false);
      setForm({ email: "", password: "", name: "", role: "client", workspace_id: "" });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const openEdit = (u) => {
    setEditUser(u);
    setEditForm({
      name: u.name || "",
      email: u.email || "",
      role: u.role || "client",
      workspace_id: u.workspace_id || "",
      status: u.status || "active",
    });
    setEditErrors({});
  };

  const validateEdit = () => {
    const errs = {};
    if (!editForm.name.trim()) errs.name = "Name is required";
    if (!editForm.email.trim()) errs.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(editForm.email)) errs.email = "Invalid email format";
    const dup = users.find((u) => u.email.toLowerCase() === editForm.email.toLowerCase() && u.id !== editUser?.id);
    if (dup) errs.email = "Email already in use";
    if (editForm.role === "client" && !editForm.workspace_id) errs.workspace = "Workspace required for client role";
    setEditErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const askSave = () => {
    if (!validateEdit()) return;
    setConfirmSaveOpen(true);
  };

  const doSave = async () => {
    setConfirmSaveOpen(false);
    setEditBusy(true);
    try {
      const payload = {
        name: editForm.name.trim(),
        email: editForm.email.trim().toLowerCase(),
        role: editForm.role,
        workspace_id: editForm.role === "admin" ? null : (editForm.workspace_id || null),
        status: editForm.status,
      };
      const emailChanged = payload.email !== editUser.email.toLowerCase();
      await api.patch(`/admin/users/${editUser.id}`, payload);
      if (emailChanged) toast.success("Email updated successfully.");
      else toast.success("User updated successfully.");
      setEditUser(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save changes.");
    } finally {
      setEditBusy(false);
    }
  };

  const openReset = (u) => {
    setResetTarget(u);
    setResetForm({ pw: "", pw2: "" });
    setResetOpen(true);
  };

  const doReset = async (e) => {
    e.preventDefault();
    if (resetForm.pw.length < 6) return toast.error("Password must be at least 6 characters.");
    if (resetForm.pw !== resetForm.pw2) return toast.error("Passwords do not match.");
    setResetBusy(true);
    try {
      await api.post(`/admin/users/${resetTarget.id}/reset-password`, { new_password: resetForm.pw });
      toast.success("Password reset successfully.");
      setResetOpen(false);
      setResetTarget(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to reset password.");
    } finally {
      setResetBusy(false);
    }
  };

  const doDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/admin/users/${deleteTarget.id}`);
      toast.success("User deleted.");
      setDeleteTarget(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to delete user.");
    }
  };

  const wsName = (id) => ws.find((w) => w.id === id)?.name || "—";
  const fmtDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
    catch { return iso; }
  };

  const statusBadge = (s) => {
    const map = {
      active: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/25",
      suspended: "bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/25",
      disabled: "bg-rose-500/15 text-rose-600 dark:text-rose-400 border-rose-500/25",
    };
    return <Badge variant="outline" className={`text-[10px] uppercase tracking-wider ${map[s] || map.active}`}>{s || "active"}</Badge>;
  };

  const isSelf = (u) => currentUser?.id === u.id;

  return (
    <div className="space-y-6" data-testid="admin-users-page">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Admin</p>
          <h1 className="text-3xl font-bold tracking-tight mt-1">Users</h1>
          <p className="text-sm text-muted-foreground mt-1">Grant clients access to their workspace dashboards.</p>
        </div>
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button data-testid="create-user-btn"><UserPlus className="w-4 h-4 mr-1.5" /> New user</Button>
          </DialogTrigger>
          <DialogContent data-testid="create-user-modal">
            <DialogHeader><DialogTitle>Create user</DialogTitle></DialogHeader>
            <form onSubmit={create} className="space-y-4">
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Name</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name-input" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Email</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="user-email-input" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Password</Label>
                <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required data-testid="user-password-input" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="client">Client</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {form.role === "client" && (
                <div>
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">Workspace</Label>
                  <Select value={form.workspace_id} onValueChange={(v) => setForm({ ...form, workspace_id: v })}>
                    <SelectTrigger data-testid="user-workspace-select"><SelectValue placeholder="Choose workspace" /></SelectTrigger>
                    <SelectContent>
                      {ws.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <DialogFooter>
                <Button type="submit" data-testid="user-save-btn">Create</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="p-0 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-border hover:bg-transparent">
              <TableHead className="text-[10px] uppercase tracking-wider">Name</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider">Email</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider">Role</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider">Workspace</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider">Status</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider">Last login</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.map((u) => (
              <TableRow key={u.id} className="border-border hover:bg-muted/40" data-testid={`user-row-${u.id}`}>
                <TableCell className="font-semibold text-sm">
                  {u.name || "—"}
                  {isSelf(u) && <Badge variant="outline" className="ml-2 text-[10px]">You</Badge>}
                </TableCell>
                <TableCell className="text-sm font-mono text-muted-foreground">{u.email}</TableCell>
                <TableCell><Badge variant="outline" className={u.role === "admin" ? "bg-primary/10 text-primary border-primary/25" : ""}>{u.role}</Badge></TableCell>
                <TableCell className="text-sm">{u.workspace_id ? wsName(u.workspace_id) : "—"}</TableCell>
                <TableCell>{statusBadge(u.status)}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{fmtDate(u.last_login_at)}</TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1.5">
                    <Button size="sm" variant="outline" onClick={() => openEdit(u)} data-testid={`user-edit-btn-${u.id}`} title="Edit user">
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => openReset(u)} data-testid={`user-reset-pw-btn-${u.id}`} title="Reset password">
                      <KeyRound className="w-3.5 h-3.5" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setDeleteTarget(u)}
                      data-testid={`user-delete-btn-${u.id}`}
                      className="text-destructive"
                      disabled={isSelf(u)}
                      title={isSelf(u) ? "Cannot delete your own account" : "Delete user"}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {!loading && users.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-8 text-sm text-muted-foreground">No users yet.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>

      {/* Edit user modal */}
      <Dialog open={!!editUser} onOpenChange={(o) => !o && setEditUser(null)}>
        <DialogContent className="max-w-lg" data-testid="edit-user-modal">
          <DialogHeader>
            <DialogTitle>Edit user</DialogTitle>
            <DialogDescription>Update profile, role, workspace and account status.</DialogDescription>
          </DialogHeader>
          {editUser && (
            <div className="space-y-4">
              {/* Read-only meta */}
              <div className="grid grid-cols-2 gap-3 p-3 rounded-md border border-border bg-muted/30">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">User ID</div>
                  <div className="text-xs font-mono truncate">{editUser.id}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Created</div>
                  <div className="text-xs">{fmtDate(editUser.created_at)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Last login</div>
                  <div className="text-xs">{fmtDate(editUser.last_login_at)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Current status</div>
                  <div className="text-xs">{statusBadge(editUser.status)}</div>
                </div>
              </div>

              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Full name</Label>
                <Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} data-testid="edit-user-name-input" />
                {editErrors.name && <p className="text-xs text-destructive mt-1">{editErrors.name}</p>}
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Email address</Label>
                <Input type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} data-testid="edit-user-email-input" />
                {editErrors.email && <p className="text-xs text-destructive mt-1">{editErrors.email}</p>}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">Role</Label>
                  <Select value={editForm.role} onValueChange={(v) => setEditForm({ ...editForm, role: v })}>
                    <SelectTrigger data-testid="edit-user-role-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="client">Client</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">Status</Label>
                  <Select value={editForm.status} onValueChange={(v) => setEditForm({ ...editForm, status: v })}>
                    <SelectTrigger data-testid="edit-user-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="suspended">Suspended</SelectItem>
                      <SelectItem value="disabled">Disabled</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {editForm.role === "client" && (
                <div>
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">Workspace</Label>
                  <Select value={editForm.workspace_id} onValueChange={(v) => setEditForm({ ...editForm, workspace_id: v })}>
                    <SelectTrigger data-testid="edit-user-workspace-select"><SelectValue placeholder="Choose workspace" /></SelectTrigger>
                    <SelectContent>
                      {ws.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {editErrors.workspace && <p className="text-xs text-destructive mt-1">{editErrors.workspace}</p>}
                </div>
              )}

              {isSelf(editUser) && (
                <p className="text-xs text-amber-600 dark:text-amber-400">You are editing your own account. Emergency lockout protections apply.</p>
              )}

              <DialogFooter className="gap-2 sm:gap-2">
                <Button variant="outline" onClick={() => openReset(editUser)} data-testid="edit-open-reset-btn">
                  <KeyRound className="w-3.5 h-3.5 mr-1.5" /> Reset password
                </Button>
                <Button onClick={askSave} disabled={editBusy} data-testid="edit-user-save-btn">Save changes</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Save confirmation */}
      <AlertDialog open={confirmSaveOpen} onOpenChange={setConfirmSaveOpen}>
        <AlertDialogContent data-testid="confirm-save-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Save changes to this user?</AlertDialogTitle>
            <AlertDialogDescription>
              Changes will apply immediately. If you changed the email address, the user must use the new email to sign in.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-save-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doSave} data-testid="confirm-save-confirm">Save</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="confirm-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure you want to permanently delete this user?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget && (
                <>Deleting <span className="font-semibold">{deleteTarget.name || deleteTarget.email}</span> is irreversible. They will lose access immediately.</>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} data-testid="confirm-delete-confirm" className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Delete user</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Password reset modal */}
      <Dialog open={resetOpen} onOpenChange={setResetOpen}>
        <DialogContent data-testid="reset-password-modal">
          <DialogHeader>
            <DialogTitle>Reset password</DialogTitle>
            <DialogDescription>
              {resetTarget && <>Set a new password for <span className="font-semibold">{resetTarget.name || resetTarget.email}</span>. Share it securely — the user can sign in with it immediately.</>}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={doReset} className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">New password</Label>
              <Input type="password" value={resetForm.pw} onChange={(e) => setResetForm({ ...resetForm, pw: e.target.value })} required minLength={6} data-testid="reset-pw-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Confirm password</Label>
              <Input type="password" value={resetForm.pw2} onChange={(e) => setResetForm({ ...resetForm, pw2: e.target.value })} required minLength={6} data-testid="reset-pw2-input" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setResetOpen(false)} data-testid="reset-pw-cancel">Cancel</Button>
              <Button type="submit" disabled={resetBusy} data-testid="reset-pw-confirm">Reset password</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
